import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { PERMISSIONS } from '../config/permissions';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer, PieChart, Pie, Legend
} from 'recharts';
import { AlertCircle, CheckCircle, Clock, Database, ClipboardCheck, CheckCircle2, Wrench } from 'lucide-react';
import StatusBadge from './StatusBadge';
import formatAssetId from '../utils/formatAssetId';
import VendorDashboard from './VendorDashboard';

export default function Dashboard() {
  const { dashboardType, user, hasPermission } = useAuth();
  
  // Check if user has permission to view dashboard
  if (!hasPermission(PERMISSIONS.VIEW_DASHBOARD)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-400">You don't have permission to view the dashboard.</p>
        </div>
      </div>
    );
  }
  
  // If user is a user, show the vendor-specific dashboard
  if (user?.role === 'user' || dashboardType === 'vendor') {
    return <VendorDashboard />;
  }
  
  // Otherwise, show the standard dashboard
  return <StandardDashboard />;
}

// Standard Dashboard for Admin/Customer/User roles
function StandardDashboard() {
  const { warehouses, repairs, loading, getPendingApprovals, approveInspection, organizations } = useApi();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const isCustomer = user?.role === 'user';
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Load pending approvals for customer admins
  useEffect(() => {
    if (!isCustomer && !isAdmin) return;
    if (!getPendingApprovals) return;
    
    let isMounted = true;
    
    const loadApprovals = async () => {
      try {
        setLoadingApprovals(true);
        const approvals = await getPendingApprovals();
        if (isMounted) {
          setPendingApprovals(approvals || []);
        }
      } catch (error) {
        // Function now handles errors internally, but keep this as safety
        if (isMounted) {
          setPendingApprovals([]);
        }
      } finally {
        if (isMounted) {
          setLoadingApprovals(false);
        }
      }
    };
    
    loadApprovals();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomer, isAdmin]); // getPendingApprovals intentionally excluded to prevent infinite loops

  // ResponsiveContainer handles sizing automatically, no manual resize logic needed

  // Get all assets from all warehouses
  const allAssets = useMemo(() => {
    return Object.values(warehouses).flatMap(w => w.assets || []);
  }, [warehouses]);

  // Chart Data - Asset Status Distribution
  const statusCounts = useMemo(() => {
    const counts = {};
    allAssets.forEach(asset => {
      const status = asset.status || 'Active';
      // Hide any synthetic/summary statuses from the main distribution chart
      if (status === 'Summary') return;
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [allAssets]);

  // Repair efficiency data (exclude Finalized from the efficiency view)
  const repairStatusCounts = useMemo(() => {
    const counts = {};
    repairs.forEach(repair => {
      const status = repair.status || 'Unknown';
      if (status === 'Finalized') return; // hide finalized repairs from efficiency chart
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [repairs]);

  // Recent completed repairs/activities
  const recentActivities = useMemo(() => {
    const completedRepairs = repairs
      .filter(r => r.status === 'Complete')
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      })
      .slice(0, 5);

    return completedRepairs.map(repair => {
      const asset = allAssets.find(a => a.id === repair.asset_id);
      return {
        id: repair.id,
        repair,
        assetId: asset ? formatAssetId(asset) : (repair.asset_id || 'Unknown'),
        asset: asset, // Keep asset object for formatting
        type: asset?.assetType || asset?.type || 'Unknown',
        status: repair.status,
        lastUpdated: repair.updated_at || repair.created_at
      };
    });
  }, [repairs, allAssets]);

  // Early return after all hooks
  if (loading && allAssets.length === 0) {
    return <div className={`h-full flex items-center justify-center ${isDark ? 'bg-slate-900 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>Loading dashboard...</div>;
  }

  // Stats Calculation
  const total = allAssets.length;
  const active = allAssets.filter(a => 
    (a.status === 'Active' || a.status === 'active' || !a.status)
  ).length;
  const inRepair = allAssets.filter(a => 
    a.status === 'Needs Repair' || a.status === 'needs repair'
  ).length + repairs.filter(r => r.status !== 'Complete').length;
  const scrapped = allAssets.filter(a => 
    a.status === 'Scrapped' || a.status === 'scrapped'
  ).length;

  const chartData = Object.keys(statusCounts).map(key => ({ 
    name: key, 
    value: statusCounts[key] 
  }));

  const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#6366f1', '#8b5cf6'];

  // Sent to Vendor should be shown first in repair efficiency
  const REPAIR_STATUS_ORDER = ['Sent to Vendor', 'Needs Approval', 'In Repair', 'Complete'];
  const repairChartData = [
    ...REPAIR_STATUS_ORDER.map(name => ({
      name,
      value: repairStatusCounts[name] || 0
    })),
    // include any unexpected statuses to avoid losing data
    ...Object.keys(repairStatusCounts)
      .filter(key => !REPAIR_STATUS_ORDER.includes(key))
      .map(key => ({ name: key, value: repairStatusCounts[key] }))
  ].filter(item => item.value > 0);

  const StatCard = ({ title, value, icon: Icon, color, onClick }) => {
    const handleClick =
      onClick ||
      (() => {
        // Default click simply routes to Asset Manager with no extra filters
        navigate('/assets', { state: { assetFilters: {} } });
      });

    return (
      <div
        className={`p-6 rounded-xl border ${
          isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'
        } cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-transform duration-150`}
        onClick={handleClick}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className={`text-sm font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{title}</p>
            <h3 className={`text-3xl font-bold mt-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</h3>
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon size={24} className="text-white" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`p-6 h-full overflow-y-auto space-y-6 ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
      <style>{`
        .recharts-wrapper,
        .recharts-wrapper:focus,
        .recharts-wrapper svg,
        .recharts-wrapper svg:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        .recharts-wrapper * {
          outline: none !important;
        }
        .recharts-pie path {
          stroke: none !important;
          stroke-width: 0 !important;
        }
        .recharts-tooltip-wrapper {
          pointer-events: none;
        }
      `}</style>
      <header className="mb-2">
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Dashboard</h1>
        <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Command Center Overview</p>
      </header>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Assets"
          value={total}
          icon={Database}
          color="bg-indigo-500"
        />
        <StatCard
          title="Active / In Use"
          value={active}
          icon={CheckCircle}
          color="bg-emerald-500"
          onClick={() =>
            navigate('/assets', {
              state: { assetFilters: { status: 'Active' } }
            })
          }
        />
        <StatCard
          title="In Repair Pipeline"
          value={inRepair}
          icon={Clock}
          color="bg-amber-500"
          onClick={() =>
            navigate('/assets', {
              state: { assetFilters: { status: 'Needs Repair' } }
            })
          }
        />
        <StatCard
          title="Scrapped"
          value={scrapped}
          icon={AlertCircle}
          color="bg-red-500"
          onClick={() =>
            navigate('/assets', {
              state: { assetFilters: { status: 'Scrapped' } }
            })
          }
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Chart - Asset Status Distribution */}
        <div className={`lg:col-span-2 rounded-xl p-6 border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <h3 className={`text-lg font-semibold mb-6 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>Asset Status Distribution</h3>
          <div className="w-full h-48 sm:h-56 md:h-64 outline-none focus:outline-none">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip 
                    allowEscapeViewBox={{ x: true, y: true }}
                    position={{ x: 'auto', y: 'auto' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0];
                        const index = chartData.findIndex(item => item.name === data.name);
                        const color = COLORS[index % COLORS.length];
                        const percent = ((data.value / chartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0);
                        return (
                          <div 
                            style={{ 
                              backgroundColor: color,
                              color: '#ffffff',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              marginLeft: '20px',
                              marginTop: '-10px'
                            }}
                          >
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>
                              {data.name}
                            </div>
                            <div style={{ fontSize: '12px', opacity: 0.9 }}>
                              {data.value} ({percent}%)
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className={`h-full flex items-center justify-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                No asset data available
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {chartData.map((entry, index) => (
              <div key={index} className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                ></span>
                {entry.name}
              </div>
            ))}
          </div>
        </div>

        {/* Repair Efficiency Bar Chart */}
        <div className={`rounded-xl p-6 border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <h3 className={`text-lg font-semibold mb-6 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>Repair Efficiency</h3>
          <div className="w-full h-48 sm:h-56 outline-none focus:outline-none">
            {repairChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={repairChartData}
                  margin={{ top: 10, right: 20, left: 10, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                  <XAxis
                    dataKey="name"
                    stroke={isDark ? '#94a3b8' : '#64748b'}
                    fontSize={12}
                    tickLine={false}
                    angle={-20}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis stroke={isDark ? '#94a3b8' : '#64748b'} fontSize={12} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      borderColor: isDark ? '#334155' : '#e2e8f0',
                      color: isDark ? '#f8fafc' : '#0f172a'
                    }}
                    cursor={{ fill: isDark ? '#334155' : '#f1f5f9', opacity: 0.4 }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {repairChartData.map((entry, index) => (
                      <Cell
                        key={`cell-repair-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        cursor="pointer"
                        onClick={() =>
                          navigate('/repairs', {
                            state: { repairFilters: { status: entry.name } }
                          })
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className={`h-full flex items-center justify-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                No repair data available
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {repairChartData.map((entry, index) => (
              <div key={index} className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                ></span>
                {entry.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className={`p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Recent Completed Activities</h3>
        </div>
        <div className="overflow-x-auto">
          {recentActivities.length > 0 ? (
            <table className="w-full text-left">
              <thead>
                <tr className={`text-xs uppercase ${isDark ? 'bg-slate-950 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                  <th className="px-6 py-4 font-medium">Asset ID</th>
                  <th className="px-6 py-4 font-medium">Type</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Last Updated</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-slate-800' : 'divide-gray-100'}`}>
                {recentActivities.map((activity) => (
                  <tr
                    key={activity.id}
                    className={`transition-colors cursor-pointer ${
                      isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedActivity(activity)}
                  >
                    <td className="px-6 py-4 font-mono text-sm text-blue-500">
                      {activity.assetId}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{activity.type}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={activity.status} size="sm" />
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {activity.lastUpdated 
                        ? new Date(activity.lastUpdated).toLocaleDateString()
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={`p-6 text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              No recent completed activities
            </div>
          )}
        </div>
      </div>

      {/* Pending Inspection Approvals - Customer Admin Only */}
      {(isCustomer || isAdmin) && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <ClipboardCheck size={20} className="text-amber-500" />
                Pending Inspection Approvals
                {pendingApprovals.length > 0 && (
                  <span className={`text-sm font-normal px-2 py-1 rounded-full ${isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    {pendingApprovals.length}
                  </span>
                )}
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loadingApprovals ? (
              <div className={`p-6 text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Loading pending approvals...
              </div>
            ) : pendingApprovals.length > 0 ? (
              <table className="w-full text-left">
                <thead>
                  <tr className={`text-xs uppercase ${isDark ? 'bg-slate-950 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                    <th className="px-6 py-4 font-medium">Asset ID</th>
                    <th className="px-6 py-4 font-medium">Asset Name</th>
                    <th className="px-6 py-4 font-medium">Inspection Notes</th>
                    <th className="px-6 py-4 font-medium">Performed By</th>
                    <th className="px-6 py-4 font-medium">Date</th>
                    <th className="px-6 py-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-slate-800' : 'divide-gray-100'}`}>
                  {pendingApprovals.map((approval) => (
                    <tr key={approval.id} className={`transition-colors ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'}`}>
                      <td className="px-6 py-4 font-mono text-sm text-blue-500">
                        {approval.asset_id}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {approval.asset_name || 'N/A'}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {approval.notes || 'No notes'}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {approval.performed_by_email || approval.performed_by || 'System'}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {approval.created_at ? new Date(approval.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedApproval(approval);
                              setShowApprovalModal(true);
                            }}
                            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                              isDark
                                ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 border border-blue-800/50'
                                : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200'
                            }`}
                          >
                            Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={`p-6 text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                No pending inspection approvals
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedApproval && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`border rounded-xl w-full max-w-lg mx-4 shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className={`p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Approve Failed Routine Inspection
              </h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Asset: {selectedApproval.asset_name || selectedApproval.asset_id}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Inspection Notes
                </label>
                <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-700 text-slate-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                  {selectedApproval.notes || 'No notes provided'}
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Action
                </label>
                <div className="space-y-2">
                  <button
                    onClick={async () => {
                      if (!approveInspection) return;
                      setApprovingId(selectedApproval.id);
                      try {
                        await approveInspection(selectedApproval.id, 'mark_as_good');
                        setPendingApprovals(prev => prev.filter(a => a.id !== selectedApproval.id));
                        setShowApprovalModal(false);
                        setSelectedApproval(null);
                        alert('Inspection marked as good. Asset will stay in rotation.');
                      } catch (error) {
                        alert('Failed to approve inspection: ' + (error.message || 'Unknown error'));
                      } finally {
                        setApprovingId(null);
                      }
                    }}
                    disabled={approvingId === selectedApproval.id}
                    className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      isDark
                        ? 'bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-400 border border-emerald-800/50'
                        : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200'
                    } disabled:opacity-50`}
                  >
                    <CheckCircle2 size={16} />
                    Mark as Good (Stay in Rotation)
                  </button>
                  <div>
                    <select
                      value={selectedVendor}
                      onChange={(e) => setSelectedVendor(e.target.value)}
                      className={`w-full mb-2 border rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 ${
                        isDark
                          ? 'bg-slate-950 border-slate-700 text-slate-200'
                          : 'bg-white border-gray-200 text-gray-900'
                      }`}
                    >
                      <option value="">Select Vendor for Repair</option>
                      {organizations?.filter(org => org.type === 'vendor').map(vendor => (
                        <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={async () => {
                        if (!selectedVendor) {
                          alert('Please select a vendor');
                          return;
                        }
                        if (!approveInspection) return;
                        setApprovingId(selectedApproval.id);
                        try {
                          await approveInspection(selectedApproval.id, 'send_to_vendor', selectedVendor);
                          setPendingApprovals(prev => prev.filter(a => a.id !== selectedApproval.id));
                          setShowApprovalModal(false);
                          setSelectedApproval(null);
                          setSelectedVendor('');
                          alert('Inspection approved for repair. Repair ticket has been created.');
                        } catch (error) {
                          alert('Failed to approve inspection: ' + (error.message || 'Unknown error'));
                        } finally {
                          setApprovingId(null);
                        }
                      }}
                      disabled={approvingId === selectedApproval.id || !selectedVendor}
                      className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        isDark
                          ? 'bg-amber-900/30 hover:bg-amber-900/50 text-amber-400 border border-amber-800/50'
                          : 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200'
                      } disabled:opacity-50`}
                    >
                      <Wrench size={16} />
                      Approve & Send to Vendor for Repair
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className={`p-6 border-t flex justify-end gap-3 ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedApproval(null);
                  setSelectedVendor('');
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDark
                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity Detail Modal */}
      {selectedActivity && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div
            className={`border rounded-xl w-full max-w-lg mx-4 shadow-2xl ${
              isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className={`p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Completed Activity Details
              </h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Asset {selectedActivity.assetId}
              </p>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Asset Type</span>
                <span className={isDark ? 'text-slate-200' : 'text-gray-800'}>
                  {selectedActivity.type}
                </span>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</span>
                <StatusBadge status={selectedActivity.status} size="sm" />
              </div>
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Last Updated</span>
                <span className={isDark ? 'text-slate-200' : 'text-gray-800'}>
                  {selectedActivity.lastUpdated
                    ? new Date(selectedActivity.lastUpdated).toLocaleString()
                    : 'N/A'}
                </span>
              </div>
              {selectedActivity.repair && (
                <>
                  {selectedActivity.repair.title && (
                    <div className="mt-2">
                      <p className={`text-xs uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        Repair Title
                      </p>
                      <p className={isDark ? 'text-slate-200' : 'text-gray-800'}>
                        {selectedActivity.repair.title}
                      </p>
                    </div>
                  )}
                  {selectedActivity.repair.description && (
                    <div className="mt-2">
                      <p className={`text-xs uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        Repair Description
                      </p>
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                        {selectedActivity.repair.description}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={`p-6 border-t flex justify-end gap-3 ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <button
                onClick={() => setSelectedActivity(null)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDark
                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

